import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { FILTERS } from "./importOutcomes";
import { ImportSummaryBar } from "./ImportSummaryBar";
import { ImportResultsTable } from "./ImportResultsTable";
import { AddExistingLeadDialog } from "./AddExistingLeadDialog";

const PAGE_SIZE = 50;

const download = async (batchId, filter, q) => {
    try {
        const res = await api.get(`/imports/${batchId}/results.csv`, { params: { filter, q: q || undefined }, responseType: "blob" });
        const name = /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "")?.[1] || "import-results.csv";
        const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(res.data), download: name });
        a.click();
        URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(formatApiError(e)); }
};

// Admin-only row-level results for one import (completion screen + Import History).
export const ImportResults = ({ batchId }) => {
    const [filter, setFilter] = useState("issues");
    const [page, setPage] = useState(1);
    const [q, setQ] = useState("");
    const [data, setData] = useState(null);
    const [adding, setAdding] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data: d } = await api.get(`/imports/${batchId}/results`, { params: { filter, page, page_size: PAGE_SIZE, q: q || undefined } });
            setData(d);
        } catch (e) { setData({ error: e?.response?.status === 403 ? "Detailed import results are available to administrators only." : formatApiError(e) }); }
    }, [batchId, filter, page, q]);
    useEffect(() => { load(); }, [load]);

    const pick = (f) => { setFilter(f); setPage(1); };
    const act = async (path, body) => {
        setBusy(true);
        try { await api.post(path, body); await load(); } catch (e) { toast.error(formatApiError(e)); await load(); } finally { setBusy(false); }
    };
    const onSkip = (r) => {
        const reason = window.prompt(`Skip CSV row ${r.row}? Optional reason:`, "");
        if (reason !== null) act(`/imports/${batchId}/results/${r.row}/skip`, { reason });
    };

    if (!data) return <div className="py-6 text-sm text-muted-foreground">Loading results…</div>;
    if (data.error) return <div className="py-4 text-sm text-muted-foreground" data-testid="import-results-error">{data.error}</div>;
    if (!data.details_available) return <div className="rounded-md border bg-gray-50 px-4 py-3 text-sm text-gray-700" data-testid="results-unavailable">{data.message}</div>;
    const pages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

    return (
        <div className="space-y-4 text-left" data-testid="import-results">
            <ImportSummaryBar summary={data.summary} active={filter} onPick={pick} />
            <div className="flex flex-wrap items-center gap-2">
                <Select value={filter} onValueChange={pick}>
                    <SelectTrigger className="w-60 h-9" data-testid="results-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>{FILTERS.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`filter-${k}`}>{l}</SelectItem>)}</SelectContent>
                </Select>
                <Input className="w-56 h-9" placeholder="Search name, Case ID, phone" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid="results-search" />
                {filter !== "created" && (
                    <Button variant="outline" size="sm" onClick={() => pick("created")} data-testid="view-imported-rows">View Imported Rows ({data.summary.created})</Button>
                )}
                <div className="ml-auto flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => download(batchId, "all")} data-testid="download-all-results"><Download className="h-4 w-4 mr-1" />Download Import Results</Button>
                    <Button variant="outline" size="sm" onClick={() => download(batchId, "problems")} data-testid="download-problem-rows"><Download className="h-4 w-4 mr-1" />Download Problem Rows</Button>
                </div>
            </div>
            <ImportResultsTable rows={data.rows} busy={busy} onAdd={setAdding} onSkip={onSkip}
                onLater={(r) => act(`/imports/${batchId}/results/${r.row}/review-later`)} />
            <div className="flex items-center justify-between text-xs text-muted-foreground" data-testid="results-pagination">
                <span data-testid="results-total">{data.total.toLocaleString()} row(s) · page {page} of {pages}</span>
                <div className="flex gap-2">
                    <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="results-prev">Previous</Button>
                    <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="results-next">Next</Button>
                </div>
            </div>
            {adding && <AddExistingLeadDialog batchId={batchId} row={adding} onClose={() => setAdding(null)} onDone={() => { setAdding(null); load(); }} />}
        </div>
    );
};
